import { Skip } from '@/src/utils/evaluateChain';
import { Chunk } from '@/src/core/streaming/chunk';
import { DicomFileDataLoader } from '@/src/core/streaming/dicom/dicomFileDataLoader';
import { DicomFileMetaLoader } from '@/src/core/streaming/dicom/dicomFileMetaLoader';
import { ReadDicomTagsFunction } from '@/src/core/streaming/dicom/dicomMetaLoader';
import { ImportHandler, asIntermediateResult } from '@/src/io/import/common';
import { getWorker } from '@/src/io/itk/worker';
import { FILE_EXT_TO_MIME } from '@/src/io/mimeTypes';
import { getErrorDetail } from '@/src/utils';
import { readDicomTags } from '@itk-wasm/dicom';
import { isMobileBrowser } from '@/src/utils/mobileVolume';

let pendingMobileTagRead: Promise<void> = Promise.resolve();

/**
 * Adds DICOM files to the extra context.
 * @param dataSource
 * @returns
 */
const handleDicomFile: ImportHandler = async (dataSource) => {
  if (
    dataSource.type !== 'file' ||
    dataSource.fileType !== FILE_EXT_TO_MIME.dcm
  ) {
    return Skip;
  }

  const readTags: ReadDicomTagsFunction = async (file) => {
    try {
      // A full series can contain hundreds of files. Queue mobile tag reads
      // so their File payloads do not all enter the WASM worker at once.
      const read = () => readDicomTags(file, { webWorker: getWorker() });
      const mobile = isMobileBrowser();
      const readPromise = mobile ? pendingMobileTagRead.then(read) : read();
      if (mobile) {
        pendingMobileTagRead = readPromise.then(
          () => undefined,
          () => undefined
        );
      }
      const result = await readPromise;
      return result.tags;
    } catch (error) {
      const detail = getErrorDetail(
        error,
        'the file could not be parsed as valid DICOM (check browser console for details)'
      );
      throw new Error(`Failed to read DICOM tags: ${detail}`, {
        cause: error,
      });
    }
  };

  const metaLoader = new DicomFileMetaLoader(dataSource.file, readTags);
  const dataLoader = new DicomFileDataLoader(dataSource.file);
  const chunk = new Chunk({
    metaLoader,
    dataLoader,
  });

  await chunk.loadMeta();

  return asIntermediateResult([
    {
      type: 'chunk',
      chunk,
      mime: FILE_EXT_TO_MIME.dcm,
      parent: dataSource,
    },
  ]);
};

export default handleDicomFile;
